import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Badge, Button, Dropdown } from "react-bootstrap"
import { FaRegBell } from "react-icons/fa"
import { useNavigate } from "react-router-dom"
import { getEnv } from "helper/window-helper"
import { UseAuth } from "hook/auth-hook"
import { NotificacaoPedido, NotificacaoPedidoUseCase } from "service/api/rest/notificacao-pedido-usecase"

/** Intervalo do polling do sino: contador de nao-lidas + lista, a cada 60s. */
const INTERVALO_POLLING_MS = 60000

type SinoToggleProps = {
    naoLidas: number
    onClick?: (event: React.MouseEvent) => void
}

/**
 * Botao do sino com badge de nao-lidas. forwardRef para o Dropdown do react-bootstrap
 * posicionar o menu; sem o caret padrao do toggle.
 */
const SinoToggle = React.forwardRef<HTMLButtonElement, SinoToggleProps>(({ naoLidas, onClick }, ref) => (
    <Button
        ref={ref}
        onClick={onClick}
        size="sm"
        variant="secondary"
        aria-label="Notificações"
        style={{ marginRight: "15px", position: "relative" }}
    >
        <FaRegBell size={15} />
        {naoLidas > 0 && (
            <Badge
                bg="danger"
                pill
                data-testid="sino-badge"
                aria-label={`${naoLidas} notificações não lidas`}
                style={{ position: "absolute", top: "-6px", right: "-6px" }}
            >
                {naoLidas}
            </Badge>
        )}
    </Button>
))

/**
 * Sino de notificacoes do fluxo de aprovacao exibido no header. Faz polling do contador
 * e da lista a cada 60s (intervalo limpo no unmount), abre o historico em dropdown, marca como lida
 * e navega para o detalhe do pedido. O destinatario e sempre o usuario autenticado (resolvido no
 * backend); o front nao envia id de destinatario.
 */
export const NotificacaoSino = () => {
    const navigate = useNavigate()
    const { isLoggedIn } = UseAuth()
    const endpoint = getEnv("REACT_APP_END_POINT_NOTIFICACAO_PEDIDO") ?? ""
    const service = useMemo(() => new NotificacaoPedidoUseCase(endpoint), [endpoint])

    const [naoLidas, updateNaoLidas] = useState<number>(0)
    const [notificacoes, updateNotificacoes] = useState<NotificacaoPedido[]>([])

    const carregarContador = useCallback(async () => {
        const resultado = await service.contarNaoLidas()
        if (resultado.success) updateNaoLidas(resultado.data ?? 0)
    }, [service])

    const carregarLista = useCallback(async () => {
        const resultado = await service.listar()
        // O sino exibe apenas notificações ainda não lidas — as já visualizadas
        // somem da lista (o histórico completo permanece no backend). Coerente com contarNaoLidas.
        if (resultado.success) updateNotificacoes((resultado.data ?? []).filter((n) => !n.lida))
    }, [service])

    useEffect(() => {
        // Só faz polling com sessão válida: sem login a chamada cairia no redirect + location.reload()
        // do interceptor de token (refresh infinito na tela de login). O sino monta no header global.
        if (!isLoggedIn) {
            return
        }
        carregarContador()
        carregarLista()
        const intervalo = setInterval(() => {
            carregarContador()
            carregarLista()
        }, INTERVALO_POLLING_MS)
        return () => clearInterval(intervalo)
    }, [isLoggedIn, carregarContador, carregarLista])

    const aoAbrirNotificacao = useCallback(
        async (notificacao: NotificacaoPedido) => {
            if (!notificacao.lida) {
                await service.marcarComoLida(notificacao.id)
                updateNaoLidas((atual) => Math.max(0, atual - 1))
                updateNotificacoes((atual) => atual.filter((item) => item.id !== notificacao.id))
            }
            navigate(notificacao.link ?? `/fluxo-aprovacao-pedido/${notificacao.idPedido}`)
        },
        [service, navigate]
    )

    return (
        <Dropdown align="end">
            <Dropdown.Toggle as={SinoToggle} naoLidas={naoLidas} />
            <Dropdown.Menu style={{ minWidth: "320px", maxHeight: "400px", overflowY: "auto" }}>
                <Dropdown.Header>Notificações</Dropdown.Header>
                {notificacoes.length === 0 && (
                    <Dropdown.ItemText className="text-muted">Nenhuma notificação.</Dropdown.ItemText>
                )}
                {notificacoes.map((notificacao) => (
                    <Dropdown.Item
                        key={notificacao.id}
                        onClick={() => aoAbrirNotificacao(notificacao)}
                        style={{ whiteSpace: "normal", fontWeight: notificacao.lida ? "normal" : "bold" }}
                    >
                        <div>{notificacao.mensagem}</div>
                        {notificacao.dataCriacao && <small className="text-muted">{notificacao.dataCriacao}</small>}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    )
}
